/** On-demand / Services master types (mirrors backend on_demand_masters group). */
export const ON_DEMAND_MASTER_TYPES = [
  { key: 'service_categories', label: 'Service Categories' },
  { key: 'skills', label: 'Skills' },
  { key: 'certificates', label: 'Certificates' },
  { key: 'experience_levels', label: 'Experience Levels' },
  { key: 'pricing_templates', label: 'Pricing Templates' },
  { key: 'visit_charges', label: 'Visit Charges' },
  { key: 'cancellation_reasons', label: 'Cancellation Reasons' },
] as const;

export type OnDemandMasterTypeKey = (typeof ON_DEMAND_MASTER_TYPES)[number]['key'];

/** Short helper copy shown under master type headers in Services Masters. */
export const ON_DEMAND_MASTER_HELPERS: Partial<Record<OnDemandMasterTypeKey, string>> = {
  service_categories:
    'Primary categories listed here. Use the eye icon on a row to manage its sub-categories.',
  pricing_templates:
    'Pricing template = the service package pricing model blueprint (fixed / hourly / per visit amounts used when defining packages — base labour price structure).',
  visit_charges:
    'Visit charges = the call-out / doorstep fee charged for the provider visiting the customer location (often added on top of base package price).',
};

/** Master types that auto-generate code from display name (create flow). */
export const ON_DEMAND_AUTO_CODE_TYPES = new Set<string>(
  ON_DEMAND_MASTER_TYPES.map((t) => t.key),
);

/** Uppercase SKU-style code from package name (e.g. "AC Service Basic" → "AC_SERVICE_BASIC"). */
export function slugifyPackageCode(name: string, categoryCode?: string): string {
  const fromName = name
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
  if (!fromName) return '';
  if (!categoryCode) return fromName;
  const cat = categoryCode.toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_|_$/g, '');
  if (!cat || fromName.startsWith(cat)) return fromName;
  if (fromName.length >= 6) return fromName;
  return `${cat}_${fromName}`;
}

/** Same as package slug without category prefix — used for master codes. */
export function slugifyMasterCode(name: string): string {
  return slugifyPackageCode(name);
}

/** Ensure code does not collide with existing package codes. */
export function uniquePackageCode(base: string, existingCodes: string[]): string {
  if (!base) return '';
  const set = new Set(existingCodes.map((c) => c.toUpperCase()));
  if (!set.has(base)) return base;
  let i = 2;
  while (set.has(`${base}_${i}`)) i += 1;
  return `${base}_${i}`;
}
